import { useTranslation } from "react-i18next";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Container,
  Typography,
  Card,
  CardContent,
  IconButton,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import { LegalFooter } from "@components/layout/LegalFooter";
import {
  Timer,
  Group,
  TrendingUp,
  Payments,
  DarkMode,
  LightMode,
  Language,
} from "@mui/icons-material";
import { useThemeMode } from "@/theme/ThemeContext";
import { useAppSelector } from "@/store";
import { useEffect } from "react";
import { getGoogleOAuthStartUrl } from "@/api/axios";

const features = [
  {
    icon: Timer,
    titleKey: "landing.feature1Title",
    descKey: "landing.feature1Desc",
  },
  {
    icon: Group,
    titleKey: "landing.feature2Title",
    descKey: "landing.feature2Desc",
  },
  {
    icon: TrendingUp,
    titleKey: "landing.feature3Title",
    descKey: "landing.feature3Desc",
  },
  {
    icon: Payments,
    titleKey: "landing.feature4Title",
    descKey: "landing.feature4Desc",
  },
];

export function LandingPage() {
  const { t, i18n } = useTranslation();
  const { mode, toggleTheme } = useThemeMode();
  const navigate = useNavigate();
  const { isAuthenticated, loading } = useAppSelector((s) => s.auth);

  useEffect(() => {
    if (!loading && isAuthenticated) {
      navigate("/dashboard", { replace: true });
    }
  }, [isAuthenticated, loading, navigate]);

  return (
    <Box sx={{ minHeight: "100vh" }}>
      {/* Top bar */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "space-between",
          alignItems: "center",
          rowGap: 1.5,
          columnGap: 2,
          px: { xs: 2, md: 6 },
          py: 2,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            minWidth: 0,
          }}
        >
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 1,
              bgcolor: "primary.main",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Timer sx={{ color: "white", fontSize: 22 }} />
          </Box>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 800,
              fontFamily: "'Manrope', sans-serif",
            }}
          >
            {t("app.name")}
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: { xs: "flex-end", sm: "flex-start" },
            flexWrap: "wrap",
            gap: 0.5,
            width: { xs: "100%", sm: "auto" },
          }}
        >
          <IconButton onClick={toggleTheme} size="small">
            {mode === "light" ? <DarkMode /> : <LightMode />}
          </IconButton>
          <IconButton
            onClick={() =>
              i18n.changeLanguage(i18n.language === "en" ? "hr" : "en")
            }
            size="small"
          >
            <Language />
          </IconButton>
          <Button
            variant="contained"
            href={getGoogleOAuthStartUrl()}
            size="small"
            sx={{ ml: { xs: 0, sm: 1 } }}
          >
            {t("auth.signInGoogle")}
          </Button>
        </Box>
      </Box>
      {/* Hero */}
      <Container
        maxWidth="md"
        sx={{ textAlign: "center", py: { xs: 8, md: 14 } }}
      >
        <Typography
          variant="h2"
          sx={{
            fontWeight: 800,
            fontFamily: "'Manrope', sans-serif",
            mb: 3,
            fontSize: { xs: "2rem", md: "3rem" },
          }}
        >
          {t("landing.hero")}
        </Typography>
        <Typography
          variant="h6"
          sx={{
            color: "text.secondary",
            mb: 5,
            maxWidth: 600,
            mx: "auto",
            fontWeight: 400,
            lineHeight: 1.7,
          }}
        >
          {t("landing.subtitle")}
        </Typography>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Button
            variant="contained"
            size="large"
            href={getGoogleOAuthStartUrl()}
            sx={{ px: 5, py: 1.5, fontSize: 16 }}
          >
            {t("landing.getStarted")}
          </Button>
          <Button
            variant="outlined"
            size="large"
            component={RouterLink}
            to="/preview"
            sx={{ px: 5, py: 1.5, fontSize: 16 }}
          >
            {t("preview.cta")}
          </Button>
        </Box>
      </Container>
      {/* Features */}
      <Container maxWidth="lg" sx={{ pb: 12 }}>
        <Grid container spacing={4}>
          {features.map((f) => (
            <Grid key={f.titleKey} size={{ xs: 12, sm: 6, md: 3 }}>
              <Card
                variant="outlined"
                sx={{
                  height: "100%",
                  transition: "box-shadow 0.2s",
                  "&:hover": { boxShadow: 4 },
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 2,
                      bgcolor: "primary.main",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mb: 2,
                    }}
                  >
                    <f.icon sx={{ color: "white" }} />
                  </Box>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      mb: 1,
                    }}
                  >
                    {t(f.titleKey)}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "text.secondary",
                      lineHeight: 1.7,
                    }}
                  >
                    {t(f.descKey)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
      <LegalFooter />
    </Box>
  );
}
